import {
  type SectionEnum,
  useLocationsUniformItemsCreate,
  useLocationsUniformItemsDestroy,
  useLocationsUniformItemsList,
  useUniformsCatalogList,
} from "@sidegigllc/api/client";
import { keepPreviousData } from "@tanstack/react-query";
import { COLOR_SCHEMES } from "api/resources/uniforms";
import React from "react";
import { toastApiError } from "utils/toastApiError";
import type { CustomUniformValue } from "./index";

interface UseCustomUniformCatalogParams {
  /** Location whose "Add your own" garments augment the global catalog; `null`/absent skips that fetch. */
  locationId: number | null | undefined;
  /** Only fetch while the Custom builder is shown. */
  enabled: boolean;
  value: CustomUniformValue;
  setValue: React.Dispatch<React.SetStateAction<CustomUniformValue>>;
}

/**
 * Data layer for the {@link CustomUniformBuilder}: fetches the scheme-scoped global catalog
 * (`GET /uniforms/catalog/`, CORE-6275) plus the location's own garments
 * (`GET /locations/{id}/uniform-items/`, CORE-6244), unions them for the active colour scheme, and
 * wires add/remove of "Add your own" garments. Shared by gig create (`UniformFields`) and gig edit
 * (`EditUniform`) so the fetch/union/add-remove logic lives in one place.
 */
export function useCustomUniformCatalog({ locationId, enabled, value, setValue }: UseCustomUniformCatalogParams) {
  const { data: catalog, isFetching: catalogFetching } = useUniformsCatalogList(
    { color_scheme: value.colorScheme },
    { query: { enabled, staleTime: Number.POSITIVE_INFINITY, placeholderData: keepPreviousData } },
  );
  const { data: locationItems, refetch: refetchLocationItems } = useLocationsUniformItemsList(locationId ?? 0, {
    query: {
      enabled: enabled && locationId != null,
      staleTime: Number.POSITIVE_INFINITY,
      placeholderData: keepPreviousData,
    },
  });
  const createItem = useLocationsUniformItemsCreate();
  const destroyItem = useLocationsUniformItemsDestroy();

  // The global catalog is already scheme-scoped by the backend; location garments carry their own
  // `color_schemes`, so filter them to the active scheme (neutral garments match every scheme).
  const items = [
    ...(catalog ?? []),
    ...(locationItems ?? []).filter((item) => (item.color_schemes ?? []).includes(value.colorScheme)),
  ];
  const availableItemIds = new Set(items.map((item) => item.id));
  const deletableItemIds = new Set((locationItems ?? []).map((item) => item.id));
  // The catalog (and, when a location is set, its garments) must have loaded for the CURRENT colour
  // scheme before callers filter item_ids. `keepPreviousData` keeps `catalog` defined during a scheme
  // toggle's refetch, so also require `!catalogFetching` — otherwise `availableItemIds` still holds the
  // previous scheme's ids and filtering would keep ids that no longer match the chosen scheme.
  const ready = catalog !== undefined && !catalogFetching && (locationId == null || locationItems !== undefined);

  const addCustom = async (section: SectionEnum, label: string) => {
    if (locationId == null) return;
    const created = await createItem.mutateAsync({
      locationId,
      data: { section, label, color_schemes: [...COLOR_SCHEMES] },
    });
    await refetchLocationItems();
    setValue((prev) => ({ ...prev, itemIds: [...prev.itemIds, created.id] }));
  };

  const removeCustom = async (itemId: number) => {
    if (locationId == null) return;
    try {
      await destroyItem.mutateAsync({ locationId, itemId });
      await refetchLocationItems();
      setValue((prev) => ({ ...prev, itemIds: prev.itemIds.filter((id) => id !== itemId) }));
    } catch (error) {
      toastApiError(error);
    }
  };

  return { items, availableItemIds, deletableItemIds, ready, addCustom, removeCustom };
}
